import {
	broadcastOptions,
	radioConstants,
	securityTypeOptions,
	securityTypeOptionsTip,
	ssidErrorConstants,
	vlanPriorityOptions,
} from './ssidConstants';
export const getAuthTypeValue = (value) => {
	const option = securityTypeOptions.find(
		(item) => item.label.toString() === value.toString()
	);
	return option ? option.value : '';
};

/**
 * Map API `auth_type` (numeric code or label) to form securityType option value.
 * @param {string|number} authType Raw auth_type from API or table row.
 * @return {string} Form value for CustomSelectField (e.g. `'3'`).
 */
export const resolveSecurityTypeFormValue = (authType) => {
	if (authType === undefined || authType === null || authType === '') {
		return '';
	}
	const str = String(authType).trim();
	const byValue = securityTypeOptions.find(
		(item) => String(item.value) === str
	);
	if (byValue) {
		return String(byValue.value);
	}
	const byLabel = securityTypeOptions.find(
		(item) => String(item.label) === str
	);
	if (byLabel) {
		return String(byLabel.value);
	}
	return getAuthTypeValue(str) || str;
};

/**
 * Map an API security code or label to its Canvas display label.
 * @param {string|number} authType Raw security type from the API.
 * @return {string} Canvas security label, or a safe textual value when unknown.
 */
export const resolveSecurityTypeLabel = (authType) => {
	if (authType === undefined || authType === null || authType === '') {
		return '';
	}
	const value = String(authType).trim();
	const option = [...securityTypeOptions, ...securityTypeOptionsTip].find(
		(item) =>
			String(item.value) === value ||
			String(item.label).toLowerCase() === value.toLowerCase()
	);
	if (option) return option.label;
	return /^\d+$/.test(value) ? '' : value;
};

export const getAuthTypeValueTip = (value) => {
	const option = securityTypeOptionsTip.find(
		(item) => item.label.toString() === value.toString()
	);
	return option ? option.value : '';
};

export const getVlanPriorityValue = (value) => {
	const option = vlanPriorityOptions.find(
		(item) => item.label.toString() === value.toString()
	);
	return option ? option.value : '';
};

export const getRadioValue = (value) => {
	const option = radioConstants.find(
		(item) => item.label.toString() === value.toString()
	);
	return option ? option.value : '';
};

/**
 * Template API returns `broadcast_ssid` as 0/1; setEditFields/getBroadcastValue expect Yes/No labels.
 * Hidden SSID checkbox is inverted: broadcast 1 (Yes) = not hidden, broadcast 0 (No) = hidden.
 * @param {string|number} broadcastSsid Raw broadcast_ssid from template API.
 * @return {string|undefined} Label `Yes` or `No` for setEditFields, or original when unknown.
 */
export const normalizeTemplateBroadcastSsid = (broadcastSsid) => {
	if (
		broadcastSsid === null ||
		broadcastSsid === undefined ||
		String(broadcastSsid).trim() === ''
	) {
		return broadcastSsid;
	}

	const str = String(broadcastSsid).trim();
	if (str === 'Yes' || str === 'No') {
		return str;
	}

	const byValue = broadcastOptions.find(
		(item) => item.value.toString() === str
	);
	if (byValue) {
		return byValue.label;
	}

	const num = parseInt(str, 10);
	if (!Number.isNaN(num) && (num === 0 || num === 1)) {
		return num === 1 ? 'Yes' : 'No';
	}

	return broadcastSsid;
};

/**
 * SSID template API returns numeric codes; setEditFields expects display labels.
 * @param {object} row Template row from API / datatable.
 * @return {object} Row with radio and broadcast_ssid formatted for setEditFields.
 */
export const normalizeTemplateEditRow = (row) => {
	if (!row) {
		return row;
	}

	const normalized = { ...row };

	normalized.broadcast_ssid = normalizeTemplateBroadcastSsid(
		normalized.broadcast_ssid
	);

	const radio = normalized.radio;
	if (radio !== null && radio !== undefined && String(radio) !== '') {
		const radioNum = parseInt(radio, 10);
		if (!Number.isNaN(radioNum)) {
			if (radioNum === 2 && parseInt(normalized.ssid_barring, 10) === 1) {
				normalized.radio = 'Disabled';
			} else {
				const option = radioConstants.find(
					(item) => item.value.toString() === String(radioNum)
				);
				normalized.radio = option ? option.label : radio;
			}
		}
	}

	return normalized;
};

export const getBroadcastValue = (value) => {
	const option = broadcastOptions.find(
		(item) => item.label?.toString() === value?.toString()
	);
	return option ? option.value : '';
};

export const mapSSIDErrors = (error) => {
	const erroCode = Object.keys(error)[0];
	const errorMsg = ssidErrorConstants[erroCode];
	return errorMsg;
};
